import { useEffect, useRef, useState, type ReactNode } from 'react'
import { api, ApiError } from '../lib/api'
import { deskBody, deskFields, deskProblem, emptyDeskForm, freeSlots, refusalPlace, type DeskForm, type DeskPlace, type DeskProblem } from '../lib/bookingDesk'
import { entryOf, formRules, keptService, otherMode, startBranch, type FormRules } from '../lib/bookingFlow'
import { explain } from '../lib/staffErrors'
import { formatMoment, languageName, nameIn, STAFF_LANGUAGES } from '../lib/staffText'
import type { Appointment, BookingChannel, BookingFormField, BookingFormFields, BookingSlot, CatalogueEntry, FinMode, Service } from '../lib/types'
import { FormDialog } from './FormDialog'
import { Select } from './Select'
import { Tabs } from './Tabs'
import { useStaffText } from './StaffText'
import '../styles/desk.css'

/**
 * Book at the desk, or move a booking: service, day, a free slot from the
 * same calendar the internet books from. A new booking also takes the customer
 * fields the organisation's booking form asks, each by its mode, and a language.
 * Given a catalogue (Appointments), it books branch first and a move may change the service.
 */
export function BookDrawer({
  branchId,
  services,
  zone,
  today,
  moving,
  catalogue,
  chooseBranch,
  onClose,
  onDone,
}: {
  branchId: string
  services: Service[]
  zone?: string
  today: string
  /** A booking being moved: only its day and time are chosen. */
  moving?: Appointment
  /** The branches and their bookable services; without it, the branch's active services. */
  catalogue?: CatalogueEntry[]
  /** Draws the Branch field (several branches); else the booking is branchId's. */
  chooseBranch?: boolean
  onClose: () => void
  onDone: (a: Appointment) => void
}) {
  const { lang, say } = useStaffText()
  const [branch, setBranch] = useState(() => (catalogue && !moving ? startBranch(catalogue, branchId) : branchId))
  const entry = entryOf(catalogue, branch)
  const [form, setForm] = useState<DeskForm>(() =>
    emptyDeskForm(moving?.businessDate ?? today, moving?.serviceId ?? (catalogue ? keptService(entry, '') : undefined), lang === 'az' || lang === 'ru' ? lang : 'en'),
  )
  const at = entry?.branch.timezone ?? zone
  const changesService = Boolean(moving && catalogue)
  const options = catalogue
    ? [
        ...(moving && !entry?.services.some((s) => s.id === moving.serviceId)
          ? services.filter((s) => s.id === moving.serviceId)
          : []),
        ...(entry?.services ?? []),
      ].map((s) => ({ value: s.id, label: nameIn(s.name, lang, s.code) }))
    : services.filter((s) => s.active).map((s) => ({ value: s.id, label: nameIn(s.name, lang, s.code) }))
  const [slots, setSlots] = useState<BookingSlot[]>([])
  // Null until the form is read: FIN, contact and own fields stay hidden rather than guess.
  const [rules, setRules] = useState<FormRules | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ text: string; at: DeskPlace } | null>(null)
  const said = useRef<HTMLParagraphElement>(null)
  const set = (next: Partial<DeskForm>) => setForm((f) => ({ ...f, ...next }))
  const labelOf = (field: string | undefined) =>
    field === 'mobile'
      ? say('booking.contact.sms')
      : field === 'email'
        ? say('booking.contact.email')
        : (rules?.fields.extra.find((x) => x.key === field)?.name ?? field ?? '')
  const refuse = (err: unknown, at?: DeskPlace) => {
    const field = err instanceof ApiError && (err.code === 'FIELD_REQUIRED' || err.code === 'FIELD_SHAPE') ? err.args?.field : undefined
    setError({
      text:
        err instanceof ApiError && field
          ? say(err.code === 'FIELD_REQUIRED' ? 'errors.FIELD_REQUIRED' : 'errors.FIELD_SHAPE', { field: labelOf(field) })
          : explain(err, lang, 'common.unreachable'),
      at: at ?? (err instanceof ApiError ? refusalPlace(err.code, err.reason, field, form.channel) : 'foot'),
    })
  }
  useEffect(() => {
    if (error) said.current?.scrollIntoView({ block: 'nearest' })
  }, [error])
  // A move draws only the day and time, and an own field not drawn has no place: both are said at the foot.
  const drawn = (at: DeskPlace) => !at.startsWith('extra:') || Boolean(rules?.fields.extra.some((x) => `extra:${x.key}` === at))
  const place = error && ((moving && error.at !== 'slot') || !drawn(error.at) ? 'foot' : error.at)
  const saidAt = (at: DeskPlace) =>
    error && place === at && (
      <p ref={said} className="reception-error" role="alert">
        {error.text}
      </p>
    )

  const needs = (answer: { finMode: FinMode; channels: BookingChannel[]; fields?: BookingFormFields }) => {
    const r = formRules(answer)
    setRules(r)
    setForm((f) => ({ ...f, channel: deskFields(r.finMode, r.channels, f.channel).contact ?? f.channel }))
  }
  // The form is the organisation's: read before any service is chosen.
  useEffect(() => {
    if (moving || !branch) return
    api.bookingForm(branch).then(needs, () => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, moving])

  useEffect(() => {
    setSlots([])
    set({ start: '' })
    if (!branch || !form.serviceId || !form.day) return
    api
      .bookingSlots(branch, form.serviceId, form.day)
      .then((r) => {
        setSlots(freeSlots(r.slots))
        needs(r)
      })
      .catch((err) => refuse(err, 'slot'))
    // lang is left out on purpose: a language change must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, form.serviceId, form.day])
  const shown = deskFields(rules?.finMode ?? null, rules?.channels ?? null, form.channel)
  const fields = rules?.fields
  const other = fields ? otherMode(fields, form.channel) : 'off'
  const optional = (mode: string | undefined) => mode === 'optional' && <em className="book-optional">{say('booking.optional')}</em>

  const problem: DeskProblem | null = moving
    ? form.start
      ? null
      : { key: 'booking.noSlots' }
    : shown.noChannel
      ? { key: 'booking.noChannel' }
      : deskProblem(form, fields)

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const answer = moving
        ? await api.moveBooking(branchId, moving.id, form.start, form.serviceId !== moving.serviceId ? form.serviceId : undefined)
        : await api.bookAtDesk(branch, deskBody(form, fields))
      onDone(answer.appointment)
    } catch (err) {
      refuse(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormDialog
      title={say(changesService ? 'appointments.changeTitle' : moving ? 'booking.move' : catalogue ? 'appointments.newBooking' : 'booking.book')}
      sub={moving ? `${moving.customerName || moving.reference} · ${formatMoment(moving.startsAt, lang, { time: true, zone: at })}` : undefined}
      onClose={onClose}
      closeLabel={say('ui.close')}
      submitLabel={say(changesService ? 'common.save' : moving ? 'booking.move' : 'booking.book')}
      onSubmit={() => void submit()}
      valid={problem === null}
      busy={busy}
    >
      {catalogue && chooseBranch && !moving && (
        <label className="field">
          <span>{say('appointments.branch')}</span>
          <Select
            value={branch}
            onChange={(b) => {
              setBranch(b)
              setError(null)
              set({ serviceId: keptService(entryOf(catalogue, b), form.serviceId) })
            }}
            options={catalogue.map((e) => ({ value: e.branch.id, label: e.branch.name }))}
          />
        </label>
      )}
      {(!moving || changesService) && (
        <label className="field">
          <span>{say('booking.service')}</span>
          <Select value={form.serviceId} onChange={(serviceId) => set({ serviceId })} options={options} />
        </label>
      )}
      <label className="field">
        <span>{say('booking.day')}</span>
        <input className="input" type="date" min={today} value={form.day} onChange={(e) => set({ day: e.target.value })} />
      </label>
      <fieldset className="field">
        <legend>{say('booking.slot')}</legend>
        {slots.length === 0 ? (
          <p className="muted">{say('booking.noSlots')}</p>
        ) : (
          <div className="book-slots">
            {slots.map((s) => (
              <button
                key={s.start}
                type="button"
                className="book-slot"
                aria-pressed={form.start === s.start}
                onClick={() => set({ start: s.start })}
              >
                {formatMoment(s.start, lang, { time: true, zone: at })}
              </button>
            ))}
          </div>
        )}
        {saidAt('slot')}
      </fieldset>
      {!moving && (
        <>
          {fields?.name !== 'off' && (
            <label className="field">
              <span>
                {say('booking.name')}
                {optional(fields?.name)}
              </span>
              <input
                className="input"
                value={form.name}
                maxLength={100}
                required={fields?.name !== 'optional'}
                onChange={(e) => set({ name: e.target.value })}
              />
            </label>
          )}
          {saidAt('name')}
          {shown.fin && (
            <label className="field">
              <span>
                {say('booking.fin')}
                {optional(rules?.finMode)}
              </span>
              <input
                className="input"
                value={form.fin}
                maxLength={7}
                required={rules?.finMode === 'required'}
                autoCapitalize="characters"
                onChange={(e) => set({ fin: e.target.value })}
              />
            </label>
          )}
          {saidAt('fin')}
          {shown.noChannel && <p className="reception-error">{say('booking.noChannel')}</p>}
          {shown.contact && (
            <fieldset className="field">
              <legend>{say(form.channel === 'sms' ? 'booking.contact.sms' : 'booking.contact.email')}</legend>
              {shown.choices.length > 0 && (
                <Tabs
                  inline
                  value={form.channel}
                  onChange={(channel) => set({ channel })}
                  label={say(form.channel === 'sms' ? 'booking.contact.sms' : 'booking.contact.email')}
                  items={shown.choices.map((ch) => ({ value: ch, label: say(`booking.channel.${ch}`) }))}
                />
              )}
              <input
                className="input"
                type={form.channel === 'sms' ? 'tel' : 'email'}
                aria-label={say(form.channel === 'sms' ? 'booking.contact.sms' : 'booking.contact.email')}
                value={form.contact}
                onChange={(e) => set({ contact: e.target.value })}
              />
              {saidAt('contact')}
            </fieldset>
          )}
          {shown.contact && other !== 'off' && (
            <label className="field">
              <span>
                {say(form.channel === 'sms' ? 'booking.contact.email' : 'booking.contact.sms')}
                {optional(other)}
              </span>
              <input
                className="input"
                type={form.channel === 'sms' ? 'email' : 'tel'}
                value={form.other}
                required={other === 'required'}
                onChange={(e) => set({ other: e.target.value })}
              />
            </label>
          )}
          {saidAt('other')}
          {fields?.extra.map((x) => (
            <ExtraField
              key={x.key}
              field={x}
              value={form.extra[x.key] ?? ''}
              hint={optional(x.mode)}
              onChange={(v) => setForm((f) => ({ ...f, extra: { ...f.extra, [x.key]: v } }))}
              said={saidAt(`extra:${x.key}`)}
            />
          ))}
          <label className="field">
            <span>{say('booking.language')}</span>
            <Select
              value={form.language}
              onChange={(language) => set({ language: language as DeskForm['language'] })}
              options={STAFF_LANGUAGES.map((l) => ({ value: l, label: languageName(l) }))}
            />
          </label>
        </>
      )}
      {problem && form.start && !shown.noChannel && <p className="reception-error">{say(problem.key, { field: labelOf(problem.field) })}</p>}
      {saidAt('foot')}
    </FormDialog>
  )
}

/** One of the organisation's own fields, drawn by its type. */
function ExtraField({ field, value, hint, onChange, said }: {
  field: BookingFormField
  value: string
  hint: ReactNode
  onChange: (v: string) => void
  said: ReactNode
}) {
  const label = (
    <span>
      {field.name}
      {hint}
    </span>
  )
  if (field.type === 'boolean') {
    return (
      <>
        <label className="check">
          <input type="checkbox" checked={value === 'true'} onChange={(e) => onChange(e.target.checked ? 'true' : '')} />
          {label}
        </label>
        {said}
      </>
    )
  }
  return (
    <>
      <label className="field">
        {label}
        {field.type === 'choice' ? (
          <Select value={value} onChange={onChange} options={(field.choices ?? []).map((c) => ({ value: c, label: c }))} />
        ) : (
          <input
            className="input"
            type={field.type === 'date' ? 'date' : field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : 'text'}
            value={value}
            maxLength={field.maxLen || undefined}
            required={field.mode === 'required'}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </label>
      {said}
    </>
  )
}
